import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, BriefcaseBusiness } from 'lucide-react';
import { resolvePostAuthRoute } from '@/lib/authRedirect';
import { getFuncionarioTheme } from '@/lib/funcionarioTheme';

export function LoginFuncionarioPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const defaultTheme = getFuncionarioTheme('extremadura');

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await signInWithEmail(email.trim().toLowerCase(), password);
      if (error) {
        if (error.message?.includes('Email not confirmed')) {
          toast.error('Debes verificar tu correo antes de acceder');
          navigate('/auth/verify-required');
        } else {
          toast.error(error.message || 'Error al iniciar sesión');
        }
      } else {
        toast.success('Sesión iniciada');
        setTimeout(async () => {
          const { data: { user } } = await supabase.auth.getUser();
          const { data: profileData } = await supabase
            .from('profiles')
            .select('plan, opositor_body, autonomous_community')
            .eq('id', user?.id)
            .single();
          navigate(resolvePostAuthRoute({
            plan: profileData?.plan,
            opositorBody: (profileData as any)?.opositor_body,
            intent: 'funcionario',
          }));
        }, 400);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    localStorage.setItem('munilex_auth_intent', 'funcionario');
    const { error } = await signInWithGoogle();
    if (error) toast.error(error.message || 'Error con Google');
  };

  return (
    <div className="relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className={`absolute inset-0 opacity-[0.03] ${defaultTheme.motifClass}`} style={{ backgroundSize: '28px 28px' }} />

      <div className="relative">
        <Link to="/auth/funcionarios/register" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="h-3 w-3" /> Volver al registro
        </Link>

        {/* Header with Community Theme */}
        <div className="flex items-center gap-3 mb-6">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
            style={{ backgroundColor: defaultTheme.accent }}
          >
            <BriefcaseBusiness className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Acceso Funcionarios</h2>
            <p className="text-sm text-muted-foreground">Para profesionales de la administración pública</p>
          </div>
        </div>

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="tu@email.com"
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full h-11 px-4 rounded-lg bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-sm text-muted-foreground">o</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Button type="button" variant="outline" className="w-full" onClick={handleGoogleLogin}>
          Continuar con Google
        </Button>

        <p className="text-sm text-center text-muted-foreground mt-6">
          ¿No tienes cuenta?{' '}
          <Link to="/auth/funcionarios/register" className="text-primary hover:underline font-medium">Regístrate como funcionario</Link>
        </p>
      </div>
    </div>
  );
}
